import { Link, useParams, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Anchor, ArrowLeft, Calendar } from "lucide-react";
import { getPost, formatDate } from "@/lib/blogPosts";

/**
 * Inline-format a string with support for:
 *   - **bold** → <strong>
 *   - [label](url) → <a>
 *   - bare https:// URLs → <a> (auto-linked)
 *
 * Returns an array of React nodes. Kept intentionally small — a full
 * markdown parser is overkill for a hand-curated blog.
 */
function renderInline(text) {
    if (!text) return null;
    // Pattern order matters: markdown-links first (so we don't double-match
    // the URL inside), then bare URLs, then bold.
    const tokens = [];
    const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s)]+)/g;
    let last = 0;
    let m;
    let key = 0;
    while ((m = pattern.exec(text)) !== null) {
        if (m.index > last) tokens.push(text.slice(last, m.index));
        if (m[1] !== undefined) {
            tokens.push(<strong key={`b-${key++}`}>{m[1]}</strong>);
        } else if (m[2] !== undefined) {
            tokens.push(
                <a
                    key={`l-${key++}`}
                    href={m[3]}
                    target={m[3].startsWith("http") ? "_blank" : undefined}
                    rel={m[3].startsWith("http") ? "noopener noreferrer" : undefined}
                    style={{ color: "var(--bm-gold)", textDecoration: "underline" }}
                >
                    {m[2]}
                </a>
            );
        } else if (m[4] !== undefined) {
            tokens.push(
                <a
                    key={`u-${key++}`}
                    href={m[4]}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "var(--bm-gold)", textDecoration: "underline" }}
                >
                    {m[4]}
                </a>
            );
        }
        last = m.index + m[0].length;
    }
    if (last < text.length) tokens.push(text.slice(last));
    return tokens;
}

function BlogBody({ blocks }) {
    return blocks.map((block, i) => {
        if (typeof block === "string") {
            // Backwards-compat: legacy body arrays were plain strings.
            return (
                <p key={i} style={{ margin: "0 0 22px" }}>{renderInline(block)}</p>
            );
        }
        if (block.type === "h2") {
            return (
                <h2 key={i} className="bm-serif" style={{ fontSize: 26, fontWeight: 500, margin: "40px 0 16px", color: "var(--bm-text)", lineHeight: 1.25 }}>
                    {renderInline(block.text)}
                </h2>
            );
        }
        if (block.type === "h3") {
            return (
                <h3 key={i} className="bm-serif" style={{ fontSize: 21, fontWeight: 500, margin: "30px 0 12px", color: "var(--bm-text)", lineHeight: 1.3 }}>
                    {renderInline(block.text)}
                </h3>
            );
        }
        if (block.type === "ul") {
            return (
                <ul key={i} style={{ margin: "0 0 22px", paddingLeft: 24, lineHeight: 1.75 }}>
                    {block.items.map((item, j) => (
                        <li key={j} style={{ marginBottom: 8 }}>{renderInline(item)}</li>
                    ))}
                </ul>
            );
        }
        if (block.type === "ol") {
            return (
                <ol key={i} style={{ margin: "0 0 22px", paddingLeft: 24, lineHeight: 1.75 }}>
                    {block.items.map((item, j) => (
                        <li key={j} style={{ marginBottom: 8 }}>{renderInline(item)}</li>
                    ))}
                </ol>
            );
        }
        // Default: paragraph
        return (
            <p key={i} style={{ margin: "0 0 22px" }}>{renderInline(block.text || "")}</p>
        );
    });
}

export default function BlogPost() {
    const { slug } = useParams();
    const post = getPost(slug);

    // SEO: update <head> tags for this specific article. React 19's useEffect
    // runs after mount, which is fine — SSR crawlers get the default meta but
    // rich-preview scrapers (og:*) fetch client-side these days.
    useEffect(() => {
        if (!post) return;
        document.title = `${post.title} — Bosun's Mate`;
        const setMeta = (name, content, attr = "name") => {
            let el = document.querySelector(`meta[${attr}="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        setMeta("description", post.excerpt);
        setMeta("og:title", post.title, "property");
        setMeta("og:description", post.excerpt, "property");
        setMeta("og:image", post.heroImage, "property");
        setMeta("og:type", "article", "property");
        setMeta("og:url", `https://bosunsmate.app/blog/${post.slug}`, "property");

        // Canonical URL
        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", `https://bosunsmate.app/blog/${post.slug}`);

        // JSON-LD Article schema — helps Google index the post as an Article
        // rather than a generic web page.
        const existingLd = document.getElementById("blog-post-jsonld");
        if (existingLd) existingLd.remove();
        const ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = "blog-post-jsonld";
        ld.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.excerpt,
            image: post.heroImage,
            datePublished: post.date,
            author: { "@type": "Organization", name: post.author || "Bosun's Mate" },
            publisher: { "@type": "Organization", name: "Bosun's Mate", logo: { "@type": "ImageObject", url: "https://bosunsmate.app/icons/apple-touch-icon.png" } },
            mainEntityOfPage: `https://bosunsmate.app/blog/${post.slug}`,
        });
        document.head.appendChild(ld);
        return () => {
            const el = document.getElementById("blog-post-jsonld");
            if (el) el.remove();
        };
    }, [post]);

    if (!post) return <Navigate to="/blog" replace />;

    return (
        <div data-testid="blog-post-page" style={{ background: "var(--bm-bg)", minHeight: "100vh", color: "var(--bm-text)" }}>
            <header style={{ padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid var(--bm-border-soft)" }}>
                <Link to="/" className="bm-serif" style={{ fontSize: 22, color: "var(--bm-gold)", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <Anchor size={18} /> Bosun&apos;s Mate
                </Link>
                <nav style={{ display: "flex", gap: 20, alignItems: "center" }}>
                    <Link to="/#features" className="bm-link" style={{ fontSize: 14 }}>Features</Link>
                    <Link to="/pricing" className="bm-link" style={{ fontSize: 14 }}>Pricing</Link>
                    <Link to="/blog" className="bm-link" style={{ fontSize: 14, color: "var(--bm-gold)" }}>Blog</Link>
                    <Link to="/register" className="bm-btn bm-btn-gold" style={{ padding: "8px 16px", fontSize: 13 }}>Start free</Link>
                </nav>
            </header>

            <article style={{ maxWidth: 740, margin: "0 auto", padding: "48px 32px 80px" }}>
                <Link to="/blog" data-testid="blog-post-back" style={{ color: "var(--bm-text-muted)", textDecoration: "none", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 20 }}>
                    <ArrowLeft size={14} /> All posts
                </Link>
                <header style={{ marginBottom: 32 }}>
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--bm-text-muted)", marginBottom: 12 }}>
                        <Calendar size={12} />
                        <time dateTime={post.date}>{formatDate(post.date)}</time>
                    </div>
                    <h1 className="bm-serif" data-testid="blog-post-title" style={{ fontSize: "clamp(32px, 4.4vw, 46px)", margin: "0 0 18px", fontWeight: 500, lineHeight: 1.15 }}>
                        {post.title}
                    </h1>
                    <p style={{ fontSize: 18, color: "var(--bm-text-muted)", lineHeight: 1.6, margin: 0 }}>
                        {post.excerpt}
                    </p>
                </header>

                {post.heroImage && (
                    <img
                        src={post.heroImage}
                        alt=""
                        style={{ width: "100%", height: "auto", borderRadius: 10, marginBottom: 36, border: "1px solid var(--bm-border-soft)" }}
                    />
                )}

                <div style={{ fontSize: 17, lineHeight: 1.75, color: "var(--bm-text)" }}>
                    <BlogBody blocks={post.body} />
                </div>

                {post.cta && (
                    <div style={{
                        marginTop: 40, padding: "24px 28px",
                        border: "1px solid var(--bm-gold-dim)",
                        borderRadius: 12,
                        background: "linear-gradient(135deg, rgba(212,181,114,0.08), rgba(212,181,114,0.02))",
                        textAlign: "center",
                    }}>
                        <Link
                            to={post.cta.href}
                            data-testid="blog-post-cta"
                            className="bm-btn bm-btn-gold"
                            style={{ padding: "12px 24px", fontSize: 15, display: "inline-block" }}
                        >
                            {post.cta.label}
                        </Link>
                    </div>
                )}
            </article>

            <footer style={{ borderTop: "1px solid var(--bm-border-soft)", padding: "24px 32px", textAlign: "center", color: "var(--bm-text-dim)", fontSize: 13 }}>
                <Link to="/blog" className="bm-link">More articles</Link> · <Link to="/" className="bm-link">Home</Link> · <Link to="/pricing" className="bm-link">Pricing</Link>
            </footer>
        </div>
    );
}
